import type { ReactNode } from 'react';
import { Building2, ChevronLeft, ChevronRight, Globe, KeyRound, Layers } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { getIdpProvider } from '@/types/idp';
import type { TenantOption } from './api';
import { providerLabel } from './format';

interface TenantSelectProps {
  value: string;
  onChange: (slug: string) => void;
  tenants: TenantOption[];
}

const ICON_CLASS = 'w-5 h-5 text-slate-400';

export const TenantSelect = ({ value, onChange, tenants }: TenantSelectProps) => (
  <div className="w-64">
    <SearchableSelect
      value={value}
      onChange={onChange}
      options={[
        { value: '', label: 'All tenants', icon: <Globe className={ICON_CLASS} /> },
        { value: 'platform', label: 'Platform', icon: <Layers className={ICON_CLASS} /> },
        ...tenants.map((t) => ({
          value: t.slug,
          label: t.name,
          logo: t.logo,
          icon: t.logo ? undefined : <Building2 className={ICON_CLASS} />,
        })),
      ]}
    />
  </div>
);

const LOGO_CLASS = 'w-5 h-5 shrink-0 rounded-md object-contain bg-slate-50 dark:bg-slate-800 p-0.5';

export const TenantCell = ({
  slug,
  name,
  tenants,
}: {
  slug: string;
  name?: string | null;
  tenants: TenantOption[];
}) => {
  const match = tenants.find((t) => t.slug === slug);
  const label = name ?? match?.name ?? slug;
  return (
    <span className="inline-flex items-center gap-2 min-w-0">
      {slug === 'platform' ? (
        <Layers className={ICON_CLASS + ' shrink-0'} />
      ) : match?.logo ? (
        <img src={match.logo} alt="" className={LOGO_CLASS} />
      ) : (
        <Building2 className={ICON_CLASS + ' shrink-0'} />
      )}
      <span className="truncate">{label}</span>
    </span>
  );
};

export const ProviderCell = ({ provider }: { provider: string | null | undefined }) => {
  if (!provider) return <>—</>;
  const known = getIdpProvider(provider);
  return (
    <span className="inline-flex items-center gap-2">
      {known?.logoUrl ? (
        <img src={known.logoUrl} alt="" className={LOGO_CLASS} />
      ) : (
        <KeyRound className={ICON_CLASS + ' shrink-0'} />
      )}
      {providerLabel(provider)}
    </span>
  );
};

export const Pager = ({
  page,
  pageSize,
  total,
  onChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
}) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-500">
      <span>
        {total === 0 ? 'No results' : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)} aria-label="Previous page">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span>
          Page {page} of {pages}
        </span>
        <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => onChange(page + 1)} aria-label="Next page">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export const TableShell = ({ children, footer }: { children: ReactNode; footer?: ReactNode }) => (
  <div className="rounded-2xl border border-slate-200/70 dark:border-slate-700/60 bg-white dark:bg-slate-900 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left">{children}</table>
    </div>
    {footer}
  </div>
);

export const Th = ({ children }: { children?: ReactNode }) => (
  <th className="px-4 py-3 text-[11px] uppercase tracking-widest font-bold text-slate-400 whitespace-nowrap">
    {children}
  </th>
);

export const Td = ({ children, className = '' }: { children?: ReactNode; className?: string }) => (
  <td className={`px-4 py-3 text-slate-700 dark:text-slate-200 ${className}`}>{children}</td>
);

export const EmptyRow = ({ columns, loading }: { columns: number; loading: boolean }) => (
  <tr>
    <td colSpan={columns} className="px-4 py-10 text-center text-sm text-slate-500">
      {loading ? 'Loading…' : 'Nothing to show for these filters'}
    </td>
  </tr>
);
